import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type {
  GenerateQuoteRequest,
  GenerateQuoteResponse,
} from "../types/quote";

// ==========================================
// HOOKS PARA QUOTES
// ==========================================

/**
 * Hook para generar una cotización
 * Endpoint: POST /quote/generate
 */
export function useGenerateQuote() {
  return useMutation<GenerateQuoteResponse, Error, GenerateQuoteRequest>({
    mutationFn: async (request: GenerateQuoteRequest) => {
      const response = await apiClient.post<{
        success: boolean;
        data: GenerateQuoteResponse;
      }>("/quote/generate", request);

      return response.data;
    },
  });
}

// ==========================================
// HOOKS CON CALLBACKS
// ==========================================

interface UseGenerateQuoteWithCallbacksOptions {
  onSuccess?: (data: GenerateQuoteResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook para generar cotización con callbacks personalizados
 */
export function useGenerateQuoteWithCallbacks(
  options?: UseGenerateQuoteWithCallbacksOptions
) {
  return useMutation<GenerateQuoteResponse, Error, GenerateQuoteRequest>({
    mutationFn: async (request: GenerateQuoteRequest) => {
      const response = await apiClient.post<{
        success: boolean;
        data: GenerateQuoteResponse;
      }>("/quote/generate", request);

      return response.data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// ==========================================
// HOOKS DE UTILIDAD
// ==========================================

/**
 * Hook para generar cotización desde el carrito
 */
export function useGenerateQuoteFromCart() {
  const { mutateAsync, ...rest } = useGenerateQuote();

  const generateFromCart = (
    cartItems: Array<{
      id: string;
      name: string;
      slug: string;
      quantity: number;
      images?: Array<{ url: string }>;
      primaryImage?: string;
      description?: string;
      unitOfMeasurement?: string;
      brand?: string;
    }>,
    client?: { name?: string; email?: string; phone?: string }
  ) => {
    const items = cartItems.map((item) => ({
      productId: item.id,
      productName: item.name,
      productSlug: item.slug,
      quantity: item.quantity,
      imageUrl: item.images?.[0]?.url || item.primaryImage,
      description: item.description,
      unitOfMeasurement: item.unitOfMeasurement,
      brand: item.brand,
    }));

    return mutateAsync({ items, client });
  };

  return {
    generateFromCart,
    ...rest,
  };
}

